import { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Mail, Clock, Phone, Instagram, Facebook, Send, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import SEO from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        throw new Error('Failed to send message');
      }
      
      toast.success('Message sent! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Failed to send message. Please try again or email us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="contact-page">
      <SEO
        title="Contact Us - Custom Cake Enquiries in Dublin 7"
        description="Have a question or need a custom cake in Dublin? Contact Mad Brothers Bakery at 17 Nephin Road, Ashtown, Dublin 7. Email madbrotherscakes@gmail.com or use our contact form."
        keywords="contact bakery Dublin, custom cake enquiry Dublin, Mad Brothers Bakery contact, bakery Ashtown Dublin 7"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Bakery",
          "name": "Mad Brothers Bakery",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "17 Nephin Road, Ashtown",
            "addressLocality": "Dublin",
            "postalCode": "D07 N8K7",
            "addressCountry": "IE"
          },
          "email": "madbrotherscakes@gmail.com",
          "openingHours": ["Mo-Fr 08:00-16:00", "Sa 08:30-16:00", "Su 09:00-16:00"]
        }}
      />
      {/* Header */}
      <section className="bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="font-handwritten text-2xl text-[#8D6E63]">Get in Touch</span>
            <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl font-bold text-[#1a1a1a] mt-2">
              Contact Us
            </h1>
            <p className="text-stone-600 mt-4 max-w-2xl">
              Have a question about your order, want to discuss a custom cake, or just want to say hello? 
              We'd love to hear from you!
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-8"
            >
              <div>
                <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#1a1a1a] mb-6">
                  Visit Our Bakery
                </h2>
                
                <div className="space-y-6">
                  <div className="flex gap-4">
                    <div className="w-12 h-12 bg-[#8D6E63]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-[#8D6E63]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#1a1a1a]">Address</h3>
                      <p className="text-stone-600">
                        17 Nephin Road, Ashtown<br />
                        Dublin 7, D07 N8K7
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="w-12 h-12 bg-[#8D6E63]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-[#8D6E63]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#1a1a1a]">Email</h3>
                      <a 
                        href="mailto:madbrotherscakes@gmail.com" 
                        className="text-[#8D6E63] hover:underline"
                        data-testid="email-link"
                      >
                        madbrotherscakes@gmail.com
                      </a>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="w-12 h-12 bg-[#8D6E63]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-[#8D6E63]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#1a1a1a]">Opening Hours</h3>
                      <p className="text-stone-600">
                        Monday - Friday: 8am - 4pm<br />
                        Saturday: 8:30am - 4pm<br />
                        Sunday: 9am - 4pm
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div>
                <h3 className="font-semibold text-[#1a1a1a] mb-4">Follow Us</h3>
                <div className="flex gap-4">
                  <a 
                    href="https://www.instagram.com/madbrothers.bakery/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-[#1a1a1a] rounded-xl flex items-center justify-center hover:bg-[#8D6E63] transition-colors"
                    data-testid="instagram-btn"
                  >
                    <Instagram className="w-6 h-6 text-white" />
                  </a>
                  <a 
                    href="https://facebook.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-[#1a1a1a] rounded-xl flex items-center justify-center hover:bg-[#8D6E63] transition-colors"
                    data-testid="facebook-btn"
                  >
                    <Facebook className="w-6 h-6 text-white" />
                  </a>
                </div>
              </div>

              {/* Map Placeholder */}
              <div className="bg-stone-200 rounded-2xl h-64 flex items-center justify-center">
                <div className="text-center">
                  <MapPin className="w-8 h-8 text-stone-400 mx-auto mb-2" />
                  <p className="text-stone-500">17 Nephin Road, Dublin 7</p>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <div className="bg-white rounded-2xl p-8 shadow-sm">
                <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#1a1a1a] mb-6">
                  Send us a Message
                </h2>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Name</Label>
                      <Input 
                        id="name" 
                        placeholder="Your name" 
                        className="mt-2"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        disabled={isSubmitting}
                        data-testid="contact-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email</Label>
                      <Input 
                        id="email" 
                        type="email" 
                        placeholder="your@email.com" 
                        className="mt-2"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        disabled={isSubmitting}
                        data-testid="contact-email"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="subject">Subject</Label>
                    <Input 
                      id="subject" 
                      placeholder="What's this about?" 
                      className="mt-2"
                      required
                      value={formData.subject}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      data-testid="contact-subject"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message">Message</Label>
                    <Textarea 
                      id="message" 
                      placeholder="Tell us more..." 
                      className="mt-2 min-h-[150px]"
                      required
                      value={formData.message}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      data-testid="contact-message"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-[#1a1a1a] hover:bg-[#1a1a1a]/90 rounded-full h-12"
                    disabled={isSubmitting}
                    data-testid="contact-submit"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
